import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Types for Spotify Web Playback SDK
interface SpotifyPlayer {
  connect(): Promise<boolean>;
  disconnect(): void;
  addListener(event: string, callback: (state: any) => void): void;
  removeListener(event: string, callback?: (state: any) => void): void;
  getCurrentState(): Promise<any>;
  setName(name: string): void;
  getVolume(): Promise<number>;
  setVolume(volume: number): Promise<void>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  togglePlay(): Promise<void>;
  seek(position_ms: number): Promise<void>;
  previousTrack(): Promise<void>;
  nextTrack(): Promise<void>;
  activateElement(): Promise<void>;
}

interface SpotifyContextType {
  // Authentication
  isAuthenticated: boolean;
  isPremium: boolean;
  user: { name: string; email: string } | null;
  accessToken: string | null;
  login: () => Promise<void>;
  logout: () => void;
  
  // Player
  player: SpotifyPlayer | null;
  deviceId: string | null;
  isReady: boolean;
  isPlaying: boolean;
  currentTrack: {
    name: string;
    artist: string;
    album: string;
    uri: string;
    duration: number;
    position: number;
  } | null;
  
  // Playback controls
  play: (spotifyUri: string) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  seek: (positionMs: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
  
  // State
  error: string | null;
  loading: boolean;
}

const SpotifyContext = createContext<SpotifyContextType | null>(null);

const wait = (ms: number) => new Promise<void>((resolve) => {
  window.setTimeout(resolve, ms);
});

const waitForCondition = async (condition: () => boolean, timeoutMs: number, intervalMs: number = 100) => {
  const startedAt = Date.now();

  while (!condition()) {
    if (Date.now() - startedAt >= timeoutMs) {
      return false;
    }

    await wait(intervalMs);
  }

  return true;
};

export const getSpotifyRedirectUri = () => {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'ouderwetsegezelligheid.nl' || hostname === 'www.ouderwetsegezelligheid.nl') {
    return 'https://ouderwetsegezelligheid.nl/spotify-callback';
  }

  if (hostname === 'hitster.tveurhof.nl') {
    return 'https://hitster.tveurhof.nl/spotify-callback';
  }

  const configuredRedirectUri = process.env.REACT_APP_SPOTIFY_REDIRECT_URI;
  if (configuredRedirectUri) {
    return configuredRedirectUri;
  }

  const basePath = process.env.PUBLIC_URL || '';
  return window.location.origin + basePath + '/spotify-callback';
};

// Extend window for Spotify SDK
declare global {
  interface Window {
    Spotify: {
      Player: new (config: {
        name: string;
        getOAuthToken: (cb: (token: string) => void) => void;
        volume?: number;
      }) => SpotifyPlayer;
    };
    onSpotifyWebPlaybackSDKReady: () => void;
  }
}

export const SpotifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Auth state
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number>(0);
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  
  // Player state
  const [player, setPlayer] = useState<SpotifyPlayer | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<SpotifyContextType['currentTrack']>(null);
  
  // UI state
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const playerRef = useRef<SpotifyPlayer | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const tokenExpiryRef = useRef<number>(0);
  const deviceIdRef = useRef<string | null>(null);
  const isReadyRef = useRef(false);
  // Prevents the player from being torn down and re-created on every token refresh.
  // The player uses getOAuthToken (which reads accessTokenRef) so it doesn't need
  // to be recreated when the access token rotates.
  const playerInitializedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    tokenExpiryRef.current = tokenExpiry;
  }, [tokenExpiry]);

  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  useEffect(() => {
    isReadyRef.current = isReady;
  }, [isReady]);

  // Load tokens from localStorage on mount
  useEffect(() => {
    try {
      const storedAccessToken = localStorage.getItem('spotify_access_token');
      const storedRefreshToken = localStorage.getItem('spotify_refresh_token');
      const storedExpiry = localStorage.getItem('spotify_token_expiry');

      if (storedAccessToken) {
        setAccessToken(storedAccessToken);
      }

      if (storedRefreshToken) {
        setRefreshToken(storedRefreshToken);
      }

      if (storedExpiry) {
        const expiry = parseInt(storedExpiry, 10);
        if (!Number.isNaN(expiry)) {
          setTokenExpiry(expiry);
          if (Date.now() >= expiry && storedRefreshToken) {
            // Token expired, refresh it
            refreshAccessToken(storedRefreshToken);
          }
        }
      }
    } catch (error) {
      console.warn('Could not load Spotify tokens from localStorage:', error);
    }
  }, []);

  // Keep auth state in sync when localStorage changes in another tab/window
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.key !== 'spotify_access_token' &&
        event.key !== 'spotify_refresh_token' &&
        event.key !== 'spotify_token_expiry'
      ) {
        return;
      }

      try {
        const nextAccessToken = localStorage.getItem('spotify_access_token');
        const nextRefreshToken = localStorage.getItem('spotify_refresh_token');
        const nextExpiryRaw = localStorage.getItem('spotify_token_expiry');

        setAccessToken(nextAccessToken);
        setRefreshToken(nextRefreshToken);

        if (nextExpiryRaw) {
          const nextExpiry = parseInt(nextExpiryRaw, 10);
          setTokenExpiry(Number.isNaN(nextExpiry) ? 0 : nextExpiry);
        } else {
          setTokenExpiry(0);
        }
      } catch (error) {
        console.warn('Could not sync Spotify tokens from localStorage:', error);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Refresh token when needed
  const logout = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.disconnect();
      playerRef.current = null;
    }
    
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiry(0);
    setUser(null);
    setIsPremium(false);
    setPlayer(null);
    setDeviceId(null);
    setIsReady(false);
    setCurrentTrack(null);
    setIsPlaying(false);
    
    try {
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
      localStorage.removeItem('spotify_token_expiry');
    } catch (error) {
      console.warn('Could not clear Spotify tokens from localStorage:', error);
    }
  }, []);

  const refreshAccessToken = useCallback(async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/spotify/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: token }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const expiry = Date.now() + (data.expiresIn * 1000) - 60000;
        
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        setTokenExpiry(expiry);
        
        try {
          localStorage.setItem('spotify_access_token', data.accessToken);
          localStorage.setItem('spotify_refresh_token', data.refreshToken);
          localStorage.setItem('spotify_token_expiry', expiry.toString());
        } catch (error) {
          console.warn('Could not save Spotify tokens to localStorage:', error);
        }
      } else {
        // Refresh failed, clear tokens
        logout();
      }
    } catch (err) {
      console.error('Failed to refresh token:', err);
      logout();
    }
  }, [logout]);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!refreshToken || !tokenExpiry) return;
    
    const timeUntilExpiry = tokenExpiry - Date.now();
    if (timeUntilExpiry <= 0) {
      refreshAccessToken(refreshToken);
      return;
    }
    
    // Refresh 5 minutes before expiry
    const refreshTime = Math.max(timeUntilExpiry - 300000, 0);
    const timer = setTimeout(() => refreshAccessToken(refreshToken), refreshTime);
    
    return () => clearTimeout(timer);
  }, [tokenExpiry, refreshToken, refreshAccessToken]);

  // Fetch user info when authenticated
  useEffect(() => {
    if (!accessToken) return;
    
    const fetchUser = async () => {
      try {
        const response = await fetch('https://api.spotify.com/v1/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser({ name: data.display_name, email: data.email });
          const product = typeof data.product === 'string' ? data.product.toLowerCase() : '';
          setIsPremium(product.includes('premium'));
          return;
        }

        // 401 usually means expired/invalid token and is refreshable.
        if (response.status === 401) {
          const currentRefreshToken = refreshTokenRef.current;
          if (currentRefreshToken) {
            await refreshAccessToken(currentRefreshToken);
          }
          return;
        }

        // 403 is commonly permission/account related; retrying refresh can loop.
        if (response.status === 403) {
          setError('Spotify-account kon niet geverifieerd worden. Probeer opnieuw in te loggen.');
          return;
        }

        console.warn(`Spotify /me returned ${response.status}`);
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    
    fetchUser();
  }, [accessToken, refreshAccessToken]);

  // Initialize Web Playback SDK
  useEffect(() => {
    // Disconnect / reset when logged out
    if (!accessToken) {
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
        playerInitializedRef.current = false;
        setPlayer(null);
        setIsReady(false);
        setDeviceId(null);
      }
      return;
    }

    // Already initialized — token refresh changed accessToken but we keep the
    // same player instance (it fetches the fresh token via getOAuthToken).
    if (playerInitializedRef.current) return;
    playerInitializedRef.current = true;

    // Load Spotify SDK script if not already loaded
    if (!window.Spotify) {
      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const scheduleReconnect = (delayMs: number = 2000) => {
      if (reconnectTimerRef.current) return; // already scheduled
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        if (playerRef.current && !isReadyRef.current) {
          console.log('🎷 Spotify: auto-reconnecting...');
          playerRef.current.connect().catch(err =>
            console.error('🎷 Spotify auto-reconnect failed:', err)
          );
        }
      }, delayMs);
    };

    window.onSpotifyWebPlaybackSDKReady = () => {
      if (playerRef.current) {
        return;
      }

      const newPlayer = new window.Spotify.Player({
        name: 'Hitster Music Quiz',
        getOAuthToken: (cb) => {
          const currentAccessToken = accessTokenRef.current;
          const currentRefreshToken = refreshTokenRef.current;
          const currentTokenExpiry = tokenExpiryRef.current;

          // Refresh if expired or about to expire within 30 s
          if (Date.now() >= currentTokenExpiry - 30000 && currentRefreshToken) {
            refreshAccessToken(currentRefreshToken)
              .then(() => {
                const token =
                  localStorage.getItem('spotify_access_token') ??
                  accessTokenRef.current;
                if (token) cb(token);
                else if (currentAccessToken) cb(currentAccessToken);
              })
              .catch(() => {
                if (currentAccessToken) cb(currentAccessToken);
              });
          } else if (currentAccessToken) {
            cb(currentAccessToken);
          }
        },
        volume: 0.5,
      });

      // Error handling
      newPlayer.addListener('initialization_error', ({ message }) => {
        setError(`Initialization error: ${message}`);
      });

      newPlayer.addListener('authentication_error', ({ message }) => {
        console.warn('🎷 Spotify authentication_error:', message);
        setError(`Authentication error: ${message}`);
        // Try refreshing the token before logging out
        const rt = refreshTokenRef.current;
        if (rt) {
          refreshAccessToken(rt)
            .then(() => {
              if (playerRef.current) {
                playerRef.current.connect().catch(err =>
                  console.error('🎷 Reconnect after auth error failed:', err)
                );
              }
            })
            .catch(() => logout());
        } else {
          logout();
        }
      });

      newPlayer.addListener('account_error', ({ message }) => {
        setIsPremium(false);
        setError(`Account error: ${message}. Spotify Premium is required.`);
      });

      newPlayer.addListener('playback_error', ({ message }) => {
        // "no list was loaded" happens on first play before Spotify has context — not a real error
        if (message && message.includes('no list was loaded')) {
          console.warn('Spotify playback_error (ignored):', message);
          return;
        }
        setError(`Playback error: ${message}`);
      });

      // Ready
      newPlayer.addListener('ready', ({ device_id }) => {
        console.log('Spotify Player ready with Device ID:', device_id);
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        deviceIdRef.current = device_id;
        setDeviceId(device_id);
        isReadyRef.current = true;
        setIsReady(true);
        setIsPremium(true);
        setError(null);
      });

      // Not Ready — schedule an automatic reconnect attempt
      newPlayer.addListener('not_ready', ({ device_id }) => {
        console.log('🎷 Spotify: device went offline:', device_id);
        if (deviceIdRef.current === device_id) {
          deviceIdRef.current = null;
        }
        isReadyRef.current = false;
        setIsReady(false);
        setDeviceId((currentDeviceId) => currentDeviceId === device_id ? null : currentDeviceId);
        scheduleReconnect(2000);
      });

      // Playback state changes
      newPlayer.addListener('player_state_changed', (state) => {
        if (!state) {
          setCurrentTrack(null);
          setIsPlaying(false);
          return;
        }

        const track = state.track_window.current_track;
        setCurrentTrack({
          name: track.name,
          artist: track.artists.map((a: { name: string }) => a.name).join(', '),
          album: track.album.name,
          uri: track.uri,
          duration: state.duration,
          position: state.position,
        });
        setIsPlaying(!state.paused);
      });

      // Connect
      newPlayer.connect().then((success) => {
        if (success) {
          console.log('Spotify Player connected successfully');
          setPlayer(newPlayer);
          playerRef.current = newPlayer;
        }
      });
    };

    // If SDK is already loaded, initialize immediately
    if (window.Spotify) {
      window.onSpotifyWebPlaybackSDKReady();
    }
    // No cleanup here for the player — token refreshes should not tear down the
    // player. The unmount-only effect below handles the final disconnect.
  }, [accessToken, logout, refreshAccessToken]);

  // Unmount-only cleanup so that token refreshes never disconnect the player
  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
      playerInitializedRef.current = false;
    };
  }, []);

  // Keepalive: every 25 s check that the player is still connected and the
  // token is fresh. This recovers from silent disconnects (e.g. laptop sleep,
  // network hiccups) and prevents Spotify from revoking the token mid-session.
  useEffect(() => {
    if (!accessToken) return;

    const keepalive = setInterval(async () => {
      // Proactively refresh token if < 2 minutes left (earlier than the
      // standard 5-minute timer, so getOAuthToken never receives a stale token)
      if (refreshTokenRef.current && tokenExpiryRef.current > 0) {
        const timeLeft = tokenExpiryRef.current - Date.now();
        if (timeLeft > 0 && timeLeft <= 120000) {
          console.log('🎷 Spotify keepalive: proactive token refresh');
          refreshAccessToken(refreshTokenRef.current);
        }
      }

      // Reconnect if the player lost its device
      if (playerRef.current && !isReadyRef.current) {
        console.log('🎷 Spotify keepalive: player not ready, reconnecting...');
        try {
          await playerRef.current.connect();
        } catch (err) {
          console.error('🎷 Spotify keepalive reconnect failed:', err);
        }
        return;
      }

      // Ping the player to confirm it is still alive
      if (playerRef.current && isReadyRef.current) {
        try {
          await playerRef.current.getCurrentState();
        } catch (err) {
          console.warn('🎷 Spotify keepalive ping failed — reconnecting:', err);
          isReadyRef.current = false;
          setIsReady(false);
          try {
            await playerRef.current.connect();
          } catch (reconnectErr) {
            console.error('🎷 Spotify keepalive reconnect failed:', reconnectErr);
          }
        }
      }
    }, 25000);

    return () => clearInterval(keepalive);
  }, [accessToken, refreshAccessToken]);

  const transferPlaybackToDevice = useCallback(async (targetDeviceId: string) => {
    if (!accessToken) {
      throw new Error('Missing Spotify access token');
    }

    let response: Response;
    try {
      response = await fetch('https://api.spotify.com/v1/me/player', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_ids: [targetDeviceId],
          play: false,
        }),
      });
    } catch (error) {
      throw new Error('Transfer to Spotify device failed before a response was received');
    }

    if (!response.ok) {
      let details: any = null;
      try {
        details = await response.json();
      } catch (error) {
        details = null;
      }

      const message = details?.error?.message || `Transfer failed with status ${response.status}`;
      throw new Error(message);
    }
  }, [accessToken]);

  const playTrackDirectly = useCallback(async (spotifyUri: string, targetDeviceId: string) => {
    if (!accessToken) {
      throw new Error('Missing Spotify access token');
    }

    let response: Response;
    try {
      response = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${encodeURIComponent(targetDeviceId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uris: [spotifyUri],
          position_ms: 0,
        }),
      });
    } catch (error) {
      const fetchError = new Error('Spotify play request failed before a response was received') as Error & { status?: number };
      throw fetchError;
    }

    if (response.ok || response.status === 204) {
      return;
    }

    let details: any = null;
    try {
      details = await response.json();
    } catch (error) {
      details = null;
    }

    const message = details?.error?.message || `Play failed with status ${response.status}`;
    const statusError = new Error(message) as Error & { status?: number };
    statusError.status = response.status;
    throw statusError;
  }, [accessToken]);

  // Login function
  const login = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const redirectUri = getSpotifyRedirectUri();
      
      const response = await fetch(`${API_BASE}/api/spotify/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      
      if (data.url) {
        // Store current URL to return after auth
        try {
          localStorage.setItem('spotify_return_url', window.location.href);
        } catch (error) {
          console.warn('Could not save return URL to localStorage:', error);
        }
        // Redirect to Spotify auth
        window.location.href = data.url;
      } else {
        setError('Could not get login URL');
      }
    } catch (err) {
      setError('Failed to start login');
    } finally {
      setLoading(false);
    }
  }, []);

  // Playback controls
  const play = useCallback(async (spotifyUri: string) => {
    console.log('🎧 SpotifyContext.play called:', { spotifyUri, hasToken: !!accessToken, deviceId: deviceIdRef.current });
    if (!accessToken || !playerRef.current) {
      console.error('🎧 Player not ready:', { accessToken: !!accessToken, deviceId: deviceIdRef.current, hasPlayer: !!playerRef.current });
      setError('Player not ready');
      throw new Error('Player not ready');
    }
    
    setLoading(true);
    setError(null);
    try {
      await playerRef.current.activateElement();

      if (!isReadyRef.current || !deviceIdRef.current) {
        const reconnected = await playerRef.current.connect();
        if (!reconnected) {
          throw new Error('Spotify player could not reconnect');
        }

        const readyInTime = await waitForCondition(() => !!deviceIdRef.current && isReadyRef.current, 5000);
        if (!readyInTime) {
          throw new Error('Spotify player device is not ready');
        }
      }

      let activeDeviceId = deviceIdRef.current;
      if (!activeDeviceId) {
        throw new Error('Spotify device ID ontbreekt');
      }

      let lastError: Error | null = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await transferPlaybackToDevice(activeDeviceId);
          await wait(350);
          await playTrackDirectly(spotifyUri, activeDeviceId);
          return;
        } catch (directPlayError) {
          const errorWithStatus = directPlayError as Error & { status?: number };
          lastError = errorWithStatus;
          const message = errorWithStatus.message.toLowerCase();
          const isRetriable =
            errorWithStatus.status === 404 ||
            errorWithStatus.status === 409 ||
            errorWithStatus.status === 502 ||
            errorWithStatus.status === 503 ||
            errorWithStatus.status === 504 ||
            message.includes('state conflict') ||
            message.includes('device not found') ||
            message.includes('before a response was received');

          if (!isRetriable || attempt === 2) {
            throw directPlayError;
          }

          if (playerRef.current) {
            isReadyRef.current = false;
            await playerRef.current.connect();
            const readyInTime = await waitForCondition(() => !!deviceIdRef.current && isReadyRef.current, 5000);
            if (readyInTime && deviceIdRef.current) {
              activeDeviceId = deviceIdRef.current;
            }
          }
          await wait(500 * (attempt + 1));
        }
      }

      if (lastError) {
        throw lastError;
      }
    } catch (err) {
      console.error('🎧 Spotify play exception:', err);
      setError(err instanceof Error ? err.message : 'Failed to play track');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, playTrackDirectly, transferPlaybackToDevice]);

  const pause = useCallback(async () => {
    if (player) {
      await player.pause();
    }
  }, [player]);

  const resume = useCallback(async () => {
    if (player) {
      await player.resume();
    }
  }, [player]);

  const seek = useCallback(async (positionMs: number) => {
    if (player) {
      await player.seek(positionMs);
    }
  }, [player]);

  const setVolumeLevel = useCallback(async (volume: number) => {
    if (player) {
      await player.setVolume(volume);
    }
  }, [player]);

  const value: SpotifyContextType = {
    isAuthenticated: !!accessToken,
    isPremium,
    user,
    accessToken,
    login,
    logout,
    player,
    deviceId,
    isReady,
    isPlaying,
    currentTrack,
    play,
    pause,
    resume,
    seek,
    setVolume: setVolumeLevel,
    error,
    loading,
  };

  return (
    <SpotifyContext.Provider value={value}>
      {children}
    </SpotifyContext.Provider>
  );
};

export const useSpotify = () => {
  const context = useContext(SpotifyContext);
  if (!context) {
    throw new Error('useSpotify must be used within a SpotifyProvider');
  }
  return context;
};

// Handle OAuth callback
export const handleSpotifyCallback = async (code: string): Promise<boolean> => {
  try {
    const redirectUri = getSpotifyRedirectUri();
    const response = await fetch(`${API_BASE}/api/spotify/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirectUri }),
    });
    
    if (response.ok) {
      const data = await response.json();
      const expiry = Date.now() + (data.expiresIn * 1000) - 60000;
      
      try {
        localStorage.setItem('spotify_access_token', data.accessToken);
        localStorage.setItem('spotify_refresh_token', data.refreshToken);
        localStorage.setItem('spotify_token_expiry', expiry.toString());
      } catch (error) {
        console.warn('Could not save Spotify tokens to localStorage:', error);
      }
      
      return true;
    }
  } catch (err) {
    console.error('Failed to handle callback:', err);
  }
  return false;
};
